import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/utils/customBaseQuery';

import {
  DefaultFilter,
  DefaultFiltersResponse,
  IDiskSpaceFilter,
  IUserActionsResponse,
  IUserActivityFilter
} from '@/entities/admin-app/statistics';

import { IFullResponse, IQueryTable, ISimpleFiltersQuery, IQueryUsersTable } from '@/types/api';
import { IUsersInitialFilters } from '@/types/enums/users';

import { BASE_URL } from '../constants';
import { IFilterItemsRequest, IFilterItemsResponse } from '@/entities/admin-app/roles';

const tagTypes = ['Statistics', 'Filters', 'StatisticsList'];

export const statisticsApi = createApi({
  reducerPath: 'api/statistics',
  baseQuery: customBaseQuery(`${BASE_URL}/v2/`),

  refetchOnFocus: false,
  tagTypes: tagTypes,
  endpoints: (build) => ({
    getStatistics: build.query({
      query: (payload?: IQueryTable<IUsersInitialFilters>) => ({
        url: `Data`,
        body: {
          ...payload
        },
        method: 'post'
      })
    }),
    getUsersActionsList: build.query<IUserActionsResponse, ISimpleFiltersQuery>({
      query: (payload) => ({
        url: `/useractions/list`,

        body: {
          ...payload
        },
        method: 'post'
      }),
      transformResponse: (response: IFullResponse<any>) => response.Response.Data
    }),
    getDefaultUsersActionsFilters: build.query<DefaultFilter[], number>({
      query: (count: number) => ({
        url: `useractions/filters/default?count=${count}`,
        method: 'get'
      }),
      transformResponse: (response: IFullResponse<DefaultFiltersResponse>) =>
        response.Response.Data.DefaultFilters
    }),
    getUsersActionsFilter: build.query<IFilterItemsResponse, IFilterItemsRequest>({
      query: (payload: IFilterItemsRequest) => ({
        url: `useractions/filter`,
        body: {
          ...payload
        },
        method: 'post'
      }),
      transformResponse: (response: IFullResponse<IFilterItemsResponse>) => {
        const { Data: data } = response.Response;

        data.FilterItems.forEach((item) => {
          delete item.Count;
        });

        return data;
      }
    }),
    getUsersActionsReport: build.query<any, ISimpleFiltersQuery>({
      query: (payload) => ({
        url: `useractions/export`,
        body: {
          ...payload
        },
        method: 'post'
      }),
      transformResponse: (response: IFullResponse<any>) => response.Response.Data
    }),
    getDiskSpace: build.query<IDiskSpaceFilter, IQueryUsersTable>({
      query: (payload) => ({
        url: `statistics/userspace`,
        body: {
          ...payload
        },
        method: 'post'
      }),
      transformResponse: (response: IFullResponse<any>) => response.Response.Data
    }),
    getUserActivity: build.query<IUserActivityFilter, IQueryUsersTable>({
      query: (payload) => ({
        url: `statistics/useractivity`,
        body: {
          ...payload
        },
        method: 'post'
      }),
      transformResponse: (response: IFullResponse<any>) => response.Response.Data
    })
  })
});

export const {
  useGetStatisticsQuery,
  useGetUsersActionsListQuery,
  useLazyGetUsersActionsReportQuery,
  useGetDefaultUsersActionsFiltersQuery,
  useGetUsersActionsFilterQuery,
  useGetDiskSpaceQuery,
  useLazyGetDiskSpaceQuery,
  useGetUserActivityQuery,
  useLazyGetUserActivityQuery
} = statisticsApi;
